import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button, Input } from '../../components/ui'
import { Logo } from '../../components/ui/Logo'
import { useAuth } from '../../context/AuthContext'
import { authApi } from '../../lib/api/auth'
import { ApiRequestError } from '../../lib/api/client'
import { NexoNetworkAnimation } from './NexoNetworkAnimation'

const loginSchema = z.object({
  email: z.string().trim().min(1, 'Ingresa tu correo').email('Correo inválido'),
  password: z.string().min(1, 'Ingresa tu contraseña'),
})
type LoginValues = z.infer<typeof loginSchema>

const forgotSchema = z.object({
  email: z.string().trim().min(1, 'Ingresa tu correo').email('Correo inválido'),
})
type ForgotValues = z.infer<typeof forgotSchema>

function LoginForm({ onForgot }: { onForgot: () => void }) {
  const { setSession } = useAuth()
  const [serverError, setServerError] = useState('')
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) })

  async function onSubmit(values: LoginValues) {
    setServerError('')
    try {
      const session = await authApi.login(values.email, values.password)
      setSession(session)
    } catch (err) {
      setServerError(
        err instanceof ApiRequestError && err.status === 401 ? 'Credenciales incorrectas' : 'No se pudo conectar',
      )
    }
  }

  return (
    <>
      <p className="text-sm text-muted">Inicia sesión para continuar</p>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          type="email"
          placeholder="Correo electrónico"
          autoComplete="username"
          {...register('email')}
          error={errors.email?.message}
        />
        <Input
          type="password"
          placeholder="Contraseña"
          autoComplete="current-password"
          {...register('password')}
          error={errors.password?.message}
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>
      <button type="button" onClick={onForgot} className="text-left text-xs font-semibold text-accent">
        ¿Olvidaste tu contraseña?
      </button>
      {serverError && (
        <p role="alert" className="text-sm text-[#ff8d9a]">
          {serverError}
        </p>
      )}
    </>
  )
}

function ForgotPasswordForm({ onBack }: { onBack: () => void }) {
  const [sent, setSent] = useState(false)
  const [serverError, setServerError] = useState('')
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotValues>({ resolver: zodResolver(forgotSchema) })

  async function onSubmit(values: ForgotValues) {
    setServerError('')
    try {
      await authApi.forgotPassword(values.email)
      setSent(true)
    } catch (err) {
      setServerError(
        err instanceof ApiRequestError
          ? 'El envío de correo todavía no está configurado — pide ayuda al administrador'
          : 'No se pudo conectar',
      )
    }
  }

  if (sent) {
    return (
      <p className="text-sm text-green">
        Si ese correo está registrado, te llegará un enlace para restablecer tu contraseña.
      </p>
    )
  }

  return (
    <>
      <p className="text-sm text-muted">Te mandamos un enlace a tu correo para poner una contraseña nueva.</p>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <Input
          type="email"
          placeholder="Correo electrónico"
          autoComplete="username"
          {...register('email')}
          error={errors.email?.message}
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Enviando…' : 'Enviar enlace'}
        </Button>
      </form>
      <button type="button" onClick={onBack} className="text-left text-xs font-semibold text-accent">
        Volver a iniciar sesión
      </button>
      {serverError && (
        <p role="alert" className="text-sm text-[#ff8d9a]">
          {serverError}
        </p>
      )}
    </>
  )
}

export function LoginPage() {
  const [mode, setMode] = useState<'login' | 'forgot'>('login')

  return (
    <div className="fixed inset-0 z-50 flex overflow-hidden bg-bg">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[32rem] w-[32rem] animate-[aurora-drift_24s_ease-in-out_infinite] rounded-full bg-accent/25 blur-[140px]" />
        <div className="absolute -bottom-40 left-1/4 h-[28rem] w-[28rem] animate-[aurora-drift_28s_ease-in-out_infinite_reverse] rounded-full bg-chart-purple/20 blur-[140px]" />
        <div className="absolute top-10 right-1/4 h-64 w-64 animate-[aurora-drift_20s_ease-in-out_infinite] rounded-full bg-chart-teal/15 blur-[120px]" />
      </div>

      {/* Mitad imaginaria izquierda: la animacion de marca. Mitad derecha: la tarjeta. */}
      <div className="relative hidden flex-1 items-center justify-center lg:flex">
        <NexoNetworkAnimation />
      </div>

      <div className="relative flex flex-1 items-center justify-center px-6">
        <div className="flex w-80 max-w-full flex-col gap-5">
          <div className="flex items-center justify-center gap-2.5 text-3xl font-extrabold">
            <Logo size={32} />
            <span>
              ne<span className="text-accent">x</span>o
            </span>
          </div>
          {mode === 'login' ? (
            <LoginForm onForgot={() => setMode('forgot')} />
          ) : (
            <ForgotPasswordForm onBack={() => setMode('login')} />
          )}
        </div>
      </div>
    </div>
  )
}
